/**
 * Video Link Modal Handler Module
 *
 * Detects video links (.webm, .mp4) in richtext_content blocks and opens them
 * in a modal using the same tooltip system as measurement tooltips.
 *
 * Features:
 * - Detects video links in richtext content
 * - Opens videos in tooltip-based modal on click
 * - Autoplay, muted by default, with controls
 * - Responsive sizing (max-width: 500px)
 * - Uses CoreEventBus for event-driven architecture
 * - Follows CCP compliance patterns
 *
 * This module follows the Central Conductor Pattern.
 *
 * Dependencies:
 * - md-config-core-event-bus.js (event communication)
 * - md-config-ui-tooltip-manager.js (tooltip functionality)
 */

(function (window) {
  'use strict';

  const debugLog =
    typeof Shopify !== 'undefined' && Shopify.designMode
      ? console.log.bind(console)
      : () => {};

  let initialized = false;
  let videoLinkCounter = 0;

  // Video file extensions to detect
  const VIDEO_EXTENSIONS = ['.webm', '.mp4'];

  /**
   * Initialize video link modal handler
   */
  const initialize = () => {
    if (initialized) return;

    try {
      debugLog('🎬 Initializing Video Link Modal Handler...');

      // Wait for DOM to be fully loaded
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', setupVideoLinks);
      } else {
        setupVideoLinks();
      }

      // Subscribe to system events if EventBus is available
      if (
        window.MurphyDoorConfigurator &&
        window.MurphyDoorConfigurator.CoreEventBus
      ) {
        // Subscribe to system ready event
        window.MurphyDoorConfigurator.CoreEventBus.subscribe(
          'system:initialized',
          setupVideoLinks
        );

        // Subscribe to content updates that might add new video links
        window.MurphyDoorConfigurator.CoreEventBus.subscribe(
          'ui:content_updated',
          setupVideoLinks
        );
      }

      initialized = true;
      debugLog('✅ Video Link Modal Handler initialized');
    } catch (error) {
      console.error('⚠️ Video Link Modal Handler initialization error:', error);
    }
  };

  /**
   * Check if a URL is a video link
   * @param {string} url - URL to check
   * @returns {boolean} True if URL ends with a video extension
   */
  const isVideoLink = (url) => {
    if (!url || typeof url !== 'string') return false;
    const lowerUrl = url.toLowerCase();
    return VIDEO_EXTENSIONS.some((ext) => lowerUrl.endsWith(ext));
  };

  /**
   * Setup video links in richtext content blocks
   */
  const setupVideoLinks = () => {
    try {
      // Find all richtext content blocks using Shopify's data-block-type attribute
      const richtextBlocks = document.querySelectorAll(
        '[data-block-type="richtext_content"]'
      );

      if (richtextBlocks.length === 0) {
        debugLog('🔍 No richtext_content blocks found');
        return;
      }

      debugLog(
        `🔍 Found ${richtextBlocks.length} richtext_content blocks, scanning for video links...`
      );

      let videoLinksFound = 0;

      richtextBlocks.forEach((block) => {
        // Find all links in this richtext block
        const links = block.querySelectorAll('a[href]');

        links.forEach((link) => {
          const href = link.getAttribute('href');

          // Check if this is a video link
          if (isVideoLink(href)) {
            // Check if already processed
            if (link.hasAttribute('data-video-modal-enabled')) {
              return;
            }

            videoLinksFound++;

            // Generate unique ID for this video link
            const videoId = `video-link-${videoLinkCounter++}`;
            link.setAttribute('data-category', videoId);
            link.setAttribute('data-video-url', href);
            link.setAttribute('data-video-modal-enabled', 'true');

            // Add tooltip trigger class
            link.classList.add('murphy-door-info-icon');

            // Prevent default link behavior
            link.addEventListener('click', handleVideoLinkClick);

            // Register this video link as a custom tooltip
            registerVideoTooltip(videoId, href);

            debugLog(`✅ Registered video link modal: ${href}`);
          }
        });
      });

      if (videoLinksFound > 0) {
        debugLog(`🎬 Registered ${videoLinksFound} video link modals`);
      } else {
        debugLog('🔍 No video links found in richtext content');
      }
    } catch (error) {
      console.error('⚠️ Error setting up video links:', error);
    }
  };

  /**
   * Handle video link click
   * @param {Event} event - Click event
   */
  const handleVideoLinkClick = (event) => {
    // Prevent default link navigation
    event.preventDefault();

    const link = event.currentTarget;
    const videoUrl = link.getAttribute('data-video-url');
    const videoId = link.getAttribute('data-category');

    debugLog(`🎬 Video link clicked: ${videoId}`);

    // Check if CCP system is available
    if (
      window.MurphyDoorConfigurator &&
      window.MurphyDoorConfigurator.CoreEventBus
    ) {
      // CCP tooltip system will handle it (event bubbles to tooltip manager)
      debugLog('✅ CCP available - tooltip system will handle modal');
    } else {
      // Use fallback modal for non-CCP pages
      debugLog('ℹ️ CCP not available - using fallback modal');
      showVideoModal(videoUrl);
    }
  };

  /**
   * Create and show a simple video modal (without CCP dependency)
   * @param {string} videoUrl - URL of the video file
   */
  const showVideoModal = (videoUrl) => {
    // Remove any existing modal
    const existing = document.getElementById('video-modal-overlay');
    if (existing) existing.remove();

    // Determine video type from URL
    const videoType = videoUrl.toLowerCase().endsWith('.webm')
      ? 'video/webm'
      : 'video/mp4';

    // Create modal overlay
    const overlay = document.createElement('div');
    overlay.id = 'video-modal-overlay';
    overlay.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0, 0, 0, 0.8);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 10000;
      cursor: pointer;
    `;

    // Create modal content
    const modal = document.createElement('div');
    modal.style.cssText = `
      position: relative;
      max-width: 450px;
      width: 90%;
      cursor: default;
    `;

    // Create video element
    const video = document.createElement('video');
    video.setAttribute('webkit-playsinline', '');
    video.setAttribute('playsinline', '');
    video.setAttribute('autoplay', '');
    video.setAttribute('muted', '');
    video.setAttribute('loop', '');
    video.setAttribute('controls', '');
    video.setAttribute('controlsList', 'nodownload');
    video.setAttribute('preload', 'auto');
    video.className = 'video-modal__video';
    video.setAttribute('aria-label', 'Product video');
    video.setAttribute('title', 'Product information video');

    const source = document.createElement('source');
    source.src = videoUrl;
    source.type = videoType;
    video.appendChild(source);

    video.appendChild(
      document.createTextNode(
        `Your browser does not support the video tag. Please download the video here: ${videoUrl}`
      )
    );

    modal.appendChild(video);
    overlay.appendChild(modal);

    // Close modal on overlay click
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) {
        overlay.remove();
      }
    });

    // Prevent video clicks from closing modal
    modal.addEventListener('click', (e) => {
      e.stopPropagation();
    });

    document.body.appendChild(overlay);
    debugLog(`🎬 Video modal opened: ${videoUrl}`);
  };

  /**
   * Register video tooltip with TooltipManager (CCP system)
   * @param {string} videoId - Unique ID for this video
   * @param {string} videoUrl - URL of the video file
   */
  const registerVideoTooltip = (videoId, videoUrl) => {
    try {
      // Check if EventBus is available
      if (
        !window.MurphyDoorConfigurator ||
        !window.MurphyDoorConfigurator.CoreEventBus
      ) {
        debugLog('ℹ️ CCP system not available - using simple modal instead');
        return;
      }

      // Determine video type from URL
      const videoType = videoUrl.toLowerCase().endsWith('.webm')
        ? 'video/webm'
        : 'video/mp4';

      // Create video HTML payload matching measurement tooltip video behavior
      const videoHtml = `
        <video
          webkit-playsinline
          playsinline
          autoplay
          muted
          loop
          controls
          controlsList="nodownload"
          preload="auto"
          class="video-modal__video"
          aria-label="Product video"
          title="Product information video"
        >
          <source src="${videoUrl}" type="${videoType}" />
          Your browser does not support the video tag. Please download the video here: ${videoUrl}
        </video>
      `;

      // Register custom tooltip via EventBus
      window.MurphyDoorConfigurator.CoreEventBus.publish(
        'ui:register_custom_tooltip',
        {
          categoryId: videoId,
          tooltipData: {
            text: '', // No plain text, only HTML
            html: videoHtml,
          },
          timestamp: Date.now(),
        }
      );

      debugLog(`✅ Video tooltip registered: ${videoId}`);
    } catch (error) {
      console.error('⚠️ Error registering video tooltip:', error);
    }
  };

  // The public interface of the module
  window.MurphyDoorConfigurator = window.MurphyDoorConfigurator || {};
  window.MurphyDoorConfigurator.VideoLinkModalHandler = {
    initialize,
    setupVideoLinks,
    isVideoLink,
  };

  // Initialize when DOM is ready
  if (
    document.readyState === 'complete' ||
    document.readyState === 'interactive'
  ) {
    initialize();
  } else {
    document.addEventListener('DOMContentLoaded', initialize);
  }
})(window);
